import { area, curveMonotoneX, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';
import { useMemo } from 'react';
import { t } from '../../i18n/index.ts';
import { axisMoney, compactMoney, fullDate, linePointLabel, money, monthYear, yearOnly } from '../../lib/format.ts';
import { useWidth } from '../../lib/hooks.ts';
import type { ChartRow, Milestone, SeriesKey } from '../../lib/model.ts';
import { clamp, moneyTicks, overlaps, textWidth, yearTicks, type Box } from './chartUtils.ts';
import { ScrubOverlay, useScrub } from './useScrub.ts';

const STACK: readonly Exclude<SeriesKey, 'netWorth' | 'debt'>[] = ['cash', 'investment', 'equity'];

const FILL: Record<SeriesKey, { color: string; opacity: number }> = {
  cash: { color: 'var(--cyber)', opacity: 0.2 },
  investment: { color: 'var(--violet)', opacity: 0.32 },
  equity: { color: 'var(--mint)', opacity: 0.14 },
  debt: { color: 'var(--terracotta)', opacity: 0.4 },
  netWorth: { color: 'var(--cyber)', opacity: 1 },
};

const MILESTONE_TONE: Record<SeriesKey, { stroke: string; text: string }> = {
  netWorth: { stroke: 'var(--cyber)', text: 'var(--cyber)' },
  cash: { stroke: 'var(--cyber)', text: 'var(--cyber)' },
  investment: { stroke: 'var(--violet)', text: 'var(--violet-text)' },
  equity: { stroke: 'var(--mint)', text: 'var(--mint)' },
  debt: { stroke: 'var(--terracotta)', text: 'var(--terracotta-text)' },
};

interface Stacked {
  readonly row: ChartRow;
  readonly bands: Partial<Record<SeriesKey, [number, number]>>;
}

function stack(rows: readonly ChartRow[], visible: ReadonlySet<SeriesKey>): Stacked[] {
  return rows.map((row) => {
    const bands: Partial<Record<SeriesKey, [number, number]>> = {};
    let top = 0;
    let bottom = 0;
    for (const key of STACK) {
      if (!visible.has(key)) continue;
      const v = row[key];
      if (v >= 0) {
        bands[key] = [top, top + v];
        top += v;
      } else {
        bands[key] = [bottom + v, bottom];
        bottom += v;
      }
    }
    if (visible.has('debt')) {
      bands.debt = [bottom - Math.abs(row.debt), bottom];
      bottom -= Math.abs(row.debt);
    }
    return { row, bands };
  });
}

/** The chart's accessible name: from now to the end of its last month. */
export const forecastChartLabel = (rows: readonly ChartRow[]): string => {
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (!first || !last) return '';
  return t('forecast.chart.label', {
    from: yearOnly(first.date), to: yearOnly(last.date), date: fullDate(first.date), start: money(first.netWorth), end: money(last.netWorth), last: monthYear(last.date),
  });
};

export function ForecastChart({
  rows, visible, milestones, height, compact = false, label, now,
}: {
  rows: readonly ChartRow[];
  visible: ReadonlySet<SeriesKey>;
  milestones: readonly Milestone[];
  height: number;
  compact?: boolean;
  label: string;
  /** Now's net worth, named under the chart: the line's first point (R20). */
  now?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const margin = compact ? { top: 10, right: 8, bottom: 24, left: 46 } : { top: 14, right: 14, bottom: 28, left: 54 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const layout = useMemo(() => {
    if (rows.length < 2 || innerW <= 0) return null;
    const stacked = stack(rows, visible);
    let hi = 0;
    let lo = 0;
    for (const s of stacked) {
      for (const band of Object.values(s.bands)) {
        if (!band) continue;
        hi = Math.max(hi, band[1]);
        lo = Math.min(lo, band[0]);
      }
      if (visible.has('netWorth')) {
        hi = Math.max(hi, s.row.netWorth);
        lo = Math.min(lo, s.row.netWorth);
      }
    }
    if (hi === lo) hi = lo + 1000;
    const { ticks, lo: yLo, hi: yHi } = moneyTicks(lo, hi, 4);
    const first = rows[0]!.date;
    const last = rows[rows.length - 1]!.date;
    const x = scaleLinear().domain([first, last]).range([0, innerW]);
    const y = scaleLinear().domain([yLo, yHi]).range([innerH, 0]);
    const xs = rows.map((r) => x(r.date));

    const areas = ([...STACK, 'debt'] as SeriesKey[])
      .filter((key) => visible.has(key))
      .map((key) => {
        const gen = area<Stacked>()
          .x((s) => x(s.row.date))
          .y0((s) => y(s.bands[key]?.[0] ?? 0))
          .y1((s) => y(s.bands[key]?.[1] ?? 0))
          .curve(curveMonotoneX);
        return { key, d: gen(stacked) ?? '' };
      });
    const nwPath = line<ChartRow>().x((r) => x(r.date)).y((r) => y(r.netWorth)).curve(curveMonotoneX)(rows) ?? '';

    const xTicks = yearTicks(first, last, Math.max(3, Math.floor(innerW / (compact ? 56 : 84))));

    // Milestone callouts: above the point when there's room, else below, then stacked further out or slid
    // sideways. A label that still collides is left off; its dot stays and the list under the chart names it.
    const titleSize = compact ? 11 : 12;
    const subSize = compact ? 10 : 11;
    const boxH = compact ? 34 : 38;
    const points = milestones.map((m) => ({ px: x(m.date), py: y(m.series === 'debt' ? 0 : m.value) }));
    const dot = compact ? 7 : 9;
    const placed: Box[] = points.map(({ px, py }) => ({ x: px - dot, y: py - dot, w: dot * 2, h: dot * 2 }));
    const stem = (box: Box, px: number, py: number): Box => {
      const above = box.y + box.h <= py;
      const top = above ? box.y + box.h : py + dot;
      const bottom = above ? py - dot : box.y;
      return { x: px - 1, y: top, w: 2, h: Math.max(0, bottom - top) };
    };
    const callouts = milestones
      .filter((m) => visible.has(m.series) || (m.series === 'debt' && visible.has('netWorth')) || visible.has('netWorth'))
      .slice()
      .sort((a, b) => a.date - b.date)
      .map((m) => {
        const px = x(m.date);
        const py = y(m.series === 'debt' ? 0 : m.value);
        const date = monthYear(m.date);
        const w = Math.min(innerW, Math.ceil(Math.max(textWidth(m.label, titleSize, 700), textWidth(date, subSize, 500)) * 1.1) + 22);
        const centred = clamp(px - w / 2, 0, innerW - w);
        const xTries = [centred, clamp(px - w + 16, 0, innerW - w), clamp(px - 16, 0, innerW - w)];
        const yTries = [0, 1, 2, 3].flatMap((n) => [py - 14 - boxH - n * (boxH + 8), py + 14 + n * (boxH + 8)]);
        const clear = (c: Box) => !placed.some((p) => overlaps(p, c)) && !placed.some((p) => overlaps(p, stem(c, px, py), 0));
        let box: Box | null = null;
        for (const ty of yTries) {
          if (ty < 0 || ty + boxH > innerH) continue;
          box = xTries.map((tx) => ({ x: tx, y: ty, w, h: boxH })).find(clear) ?? null;
          if (box) break;
        }
        if (box) placed.push(box, stem(box, px, py));
        return { m, px, py, box, date };
      });

    return { x, y, xs, ticks, areas, nwPath, xTicks, callouts, stacked, titleSize, subSize };
  }, [rows, visible, milestones, innerW, innerH, compact]);

  const { index, handlers } = useScrub(layout?.xs ?? []);
  const active = index !== null && layout ? rows[index] : undefined;
  const activeX = index !== null && layout ? layout.xs[index]! : 0;

  return (
    <figure className="chart">
      <div ref={ref} className="chart__frame" style={{ height }}>
        {layout && (
          <svg width={width} height={height} aria-hidden="true" className={`chart__svg ${active ? 'chart__svg--scrubbing' : ''}`}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              <g className="chart__grid">
                {layout.ticks.map((tick) => (
                  <g key={tick}>
                    <line
                      x1={0} x2={innerW} y1={layout.y(tick)} y2={layout.y(tick)}
                      className={tick === 0 ? 'chart__zero' : 'chart__gridline'}
                    />
                    <text x={-10} y={layout.y(tick)} dy="0.34em" textAnchor="end" className="chart__axis-label">
                      {axisMoney(tick)}
                    </text>
                  </g>
                ))}
                {layout.xTicks.map((tick, i) => (
                  <text
                    key={tick}
                    x={layout.x(tick)}
                    y={innerH + (compact ? 18 : 20)}
                    textAnchor={i === 0 && layout.x(tick) < 20 ? 'start' : i === layout.xTicks.length - 1 && layout.x(tick) > innerW - 20 ? 'end' : 'middle'}
                    className="chart__axis-label"
                  >
                    {yearOnly(tick)}
                  </text>
                ))}
              </g>
              <g className="chart__reveal">
                {layout.areas.map((a) => (
                  <path key={a.key} d={a.d} fill={FILL[a.key].color} fillOpacity={FILL[a.key].opacity} />
                ))}
                {visible.has('netWorth') && (
                  <path d={layout.nwPath} fill="none" stroke="var(--cyber)" strokeWidth={compact ? 2.5 : 3} strokeLinecap="round" />
                )}
              </g>
              <g className="chart__callouts">
                {layout.callouts.map(({ m, px, py, box, date }) => {
                  const tone = MILESTONE_TONE[m.series];
                  if (!box) {
                    return (
                      <g key={m.id} className="milestone milestone--unlabelled">
                        <circle cx={px} cy={py} r={compact ? 5 : 6.5} fill={tone.stroke} stroke="var(--card)" strokeWidth={3} />
                      </g>
                    );
                  }
                  const above = box.y + box.h <= py;
                  return (
                    <g key={m.id} className="milestone">
                      <line
                        x1={px} x2={px} y1={above ? box.y + box.h : box.y} y2={py}
                        stroke={tone.stroke} strokeOpacity={0.45} strokeWidth={1}
                      />
                      <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={8} className="milestone__box" stroke={tone.stroke} />
                      <text x={box.x + box.w / 2} y={box.y + (compact ? 15 : 16)} textAnchor="middle" fill={tone.text} className="milestone__title" fontSize={layout.titleSize}>
                        {m.label}
                      </text>
                      <text x={box.x + box.w / 2} y={box.y + (compact ? 28 : 31)} textAnchor="middle" className="milestone__date" fontSize={layout.subSize}>
                        {date}
                      </text>
                      <circle cx={px} cy={py} r={compact ? 5 : 6.5} fill={tone.stroke} stroke="var(--card)" strokeWidth={3} />
                    </g>
                  );
                })}
              </g>
              {active && (
                <g className="chart__crosshair" pointerEvents="none">
                  <line x1={activeX} x2={activeX} y1={0} y2={innerH} />
                  {visible.has('netWorth') && <circle cx={activeX} cy={layout.y(active.netWorth)} r={5} fill="#fff" />}
                </g>
              )}

            </g>
          </svg>
        )}
        {layout && (
          <ScrubOverlay
            left={margin.left} top={margin.top} width={innerW} height={innerH} label={label} max={rows.length - 1} index={index} handlers={handlers}
            valuetext={active ? `${linePointLabel(active.date, index === 0)}: ${money(active.netWorth)}` : undefined}
          />
        )}
        {active && layout && (
          <div
            className="chart-tooltip"
            style={{
              left: margin.left + activeX + (activeX > innerW * 0.62 ? -12 : 12),
              top: margin.top + 8,
              transform: activeX > innerW * 0.62 ? 'translateX(-100%)' : undefined,
            }}
            aria-hidden="true"
          >
            <span className="chart-tooltip__date">{linePointLabel(active.date, index === 0)}</span>
            <span className="chart-tooltip__value">{money(active.netWorth)}</span>
            <span className="chart-tooltip__sub">
              {t('forecast.tooltip.invested', { invested: compactMoney(active.investment), debt: compactMoney(active.debt) })}
            </span>
          </div>
        )}
      </div>
      {now !== undefined && rows[0] && (
        <figcaption className="chart-start-note" data-testid="chart-start-note">
          {t('forecast.startNote', { date: fullDate(rows[0].date), now: money(now) })}
        </figcaption>
      )}
      <YearTable rows={rows} />
      <MilestoneList milestones={milestones} />
    </figure>
  );
}

/** Every goal marker on the chart by name and date, including any whose label had no room. */
function MilestoneList({ milestones }: { milestones: readonly Milestone[] }) {
  if (milestones.length === 0) return null;
  return (
    <ul className="sr-only" aria-label={t('forecast.milestones.label')}>
      {[...milestones].sort((a, b) => a.date - b.date).map((m) => (
        <li key={m.id}>{t('forecast.milestones.item', { label: m.label, date: monthYear(m.date) })}</li>
      ))}
    </ul>
  );
}

/** The chart's text alternative: net worth now, then at the end of each year. */
function YearTable({ rows }: { rows: readonly ChartRow[] }) {
  const byYear = rows.filter((r, i) => i === 0 || new Date(r.date * 86_400_000).getUTCMonth() === 11);
  return (
    <table className="sr-only">
      <caption>{t('forecast.table.caption')}</caption>
      <thead>
        <tr><th scope="col">{t('forecast.table.year')}</th><th scope="col">{t('forecast.series.netWorth')}</th></tr>
      </thead>
      <tbody>
        {byYear.map((r) => (
          <tr key={r.date}><th scope="row">{linePointLabel(r.date, r === rows[0])}</th><td>{money(r.netWorth)}</td></tr>
        ))}
      </tbody>
    </table>
  );
}
